import { CollectDataStepType } from 'src/constants/collect-data/enums'
import type {
  FormStepConfig,
  InsuranceStepConfig,
  OnboardingTemplateStep,
  StepConfig,
} from 'src/interfaces/onboardingTemplate'

const STEP_TYPES_WITH_NUMBERED_LABELS = new Set<CollectDataStepType>([
  CollectDataStepType.Form,
  CollectDataStepType.SignContract,
])

export function stepTypeUsesNumberedLabelSuffix(type: CollectDataStepType): boolean {
  return STEP_TYPES_WITH_NUMBERED_LABELS.has(type)
}

function existingStepLabels(
  steps: readonly Pick<OnboardingTemplateStep, 'collectDataTemplateStepLabel'>[],
): Set<string> {
  const labels = new Set<string>()

  for (const step of steps) {
    const trimmed = step.collectDataTemplateStepLabel.trim()

    if (trimmed !== '') {
      labels.add(trimmed)
    }
  }

  return labels
}

/**
 * Label for a newly added step. Only `form` and `sign_contract` get numeric suffixes
 * when the base label is already taken (`form2`, `sign_contract2`, …). All other types use `type`.
 */
export function labelForNewEditorStep(
  type: CollectDataStepType,
  steps: readonly Pick<OnboardingTemplateStep, 'collectDataTemplateStepLabel'>[],
  pendingLabels: ReadonlySet<string> = new Set(),
): string {
  const baseLabel = String(type)

  if (!stepTypeUsesNumberedLabelSuffix(type)) {
    return baseLabel
  }

  const usedLabels = new Set<string>([
    ...Array.from(existingStepLabels(steps)),
    ...Array.from(pendingLabels),
  ])

  if (!usedLabels.has(baseLabel)) {
    return baseLabel
  }

  let suffix = 2

  while (usedLabels.has(`${baseLabel}${suffix}`)) {
    suffix += 1
  }

  return `${baseLabel}${suffix}`
}

export function applyLabelToNewEditorStep(
  type: CollectDataStepType,
  config: StepConfig,
  label: string,
): StepConfig {
  if (type === CollectDataStepType.Form) {
    const formConfig = config as FormStepConfig

    return { ...formConfig, label }
  }

  if (type === CollectDataStepType.InsuranceCoverage) {
    const insuranceConfig = config as InsuranceStepConfig

    return { ...insuranceConfig, label }
  }

  return config
}
